import { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { getCurrentUser } from "aws-amplify/auth";

/* Community data is loaded once on mount. */
/* eslint-disable react-hooks/exhaustive-deps */

import {
  listCommunities,
  listCommunityMemberships,
  listNotifications,
} from "../graphql/queries.js";

import {
  createCommunityMembership,
  updateCommunityMembership,
} from "../graphql/mutations.js";

import { useProgress } from "../Context/ProgressContextClean.js";

import { Theme } from "../Theme.js";

/* ---------------- CLIENTS ---------------- */

const client = generateClient();

export default function Communities({ setCurrentView }) {
  const { setCommunityData, refresh } = useProgress();
  const [communities, setCommunities] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [selectedCommunityIDs, setSelectedCommunityIDs] = useState([]);
  const [revokedCommunityIDs, setRevokedCommunityIDs] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);

  /* ---------------- LOAD DATA ---------------- */

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const user = await getCurrentUser();
      const communitiesRes = await client.graphql({
        query: listCommunities,
      });

      // derive stable user id consistently (matches ProgressContext)
      const ownerId = user?.userId || user?.username || user?.attributes?.sub;

      const membershipsRes = await client.graphql({
        query: listCommunityMemberships,
        variables: {
          filter: {
            owner: { eq: ownerId },
            leftAt: { attributeExists: false },
          },
        },
      });

      const revokedRes = await client.graphql({
        authMode: "userPool",
        query: listNotifications,
        variables: {
          filter: {
            and: [
              { TargetUserId: { eq: ownerId } },
              { NotificationType: { eq: "CommunityAccessRevoked" } },
            ],
          },
          limit: 500,
        },
      });

      const communityItems = communitiesRes.data?.listCommunities?.items ?? [];
      const membershipItems =
        membershipsRes.data?.listCommunityMemberships?.items ?? [];
      const revokedItems = revokedRes?.data?.listNotifications?.items ?? [];
      const revokedIDs = Array.from(
        new Set(
          revokedItems
            .map((n) => n?.RecommendationActionFrequency)
            .filter(Boolean),
        ),
      );

      const selectedIDs = membershipItems
        .map((m) => m.communityID)
        .filter((id) => !revokedIDs.includes(id));

      setCommunities(communityItems);
      setMemberships(membershipItems);
      setSelectedCommunityIDs(selectedIDs);
      setRevokedCommunityIDs(revokedIDs);

      setCommunityData({
        communities: communityItems,
        joinedIDs: selectedIDs,
      });

      const joinedCommunityIDs = selectedIDs;

      const joinedCommunities = communityItems.filter((c) =>
        joinedCommunityIDs.includes(c.id),
      );

      setCommunityData({
        communities: joinedCommunities,
        joinedIDs: joinedCommunityIDs,
      });
    } catch (err) {
      console.error("Failed to load communities", err);
    } finally {
      setLoading(false);
    }
  }

  /* ---------------- SELECT / DESELECT (LOCAL ONLY) ---------------- */

  function toggleCommunity(community) {
    if (revokedCommunityIDs.includes(community.id)) return;
    setSelectedCommunityIDs((prev) =>
      prev.includes(community.id)
        ? prev.filter((id) => id !== community.id)
        : [...prev, community.id],
    );
  }

  /* ---------------- SAVE ALL CHANGES ---------------- */

  async function saveChangesAndContinue() {
    try {
      const user = await getCurrentUser();
      const owner = user?.userId || user?.username || user?.attributes?.sub;

      const existingIDs = memberships.map((m) => m.communityID);

      const toJoin = selectedCommunityIDs.filter(
        (id) => !existingIDs.includes(id) && !revokedCommunityIDs.includes(id),
      );

      const toLeave = memberships.filter(
        (m) => !selectedCommunityIDs.includes(m.communityID),
      );

      /* ---- JOIN NEW COMMUNITIES ---- */
      for (const communityID of toJoin) {
        await client.graphql({
          query: createCommunityMembership,
          variables: {
            input: {
              owner,
              userID: owner,
              communityID,
              role: "member",
              joinedAt: new Date().toISOString(),
            },
          },
        });
      }

      /* ---- LEAVE REMOVED COMMUNITIES ---- */
      for (const membership of toLeave) {
        await client.graphql({
          query: updateCommunityMembership,
          variables: {
            input: {
              id: membership.id,
              leftAt: new Date().toISOString(),
            },
          },
        });
      }

      /* ---- NAVIGATE ONLY AFTER SUCCESS ---- */
      // persist selections so CommunityActivity can show them immediately
      try {
        localStorage.setItem(
          "selectedCommunityIDs",
          JSON.stringify(selectedCommunityIDs),
        );
        const idNameMap = communities
          .filter((c) => selectedCommunityIDs.includes(c.id))
          .reduce((acc, c) => {
            acc[c.id] = c.name;
            return acc;
          }, {});
        localStorage.setItem("selectedCommunityMap", JSON.stringify(idNameMap));
        localStorage.setItem(
          "selectedCommunity",
          selectedCommunityIDs[0] || "ALL",
        );
      } catch (e) {
        /* ignore storage errors */
      }

      // Immediately refresh progress context so CommunityActivity sees joined communities without reload
      try {
        if (typeof refresh === "function") await refresh();
      } catch (e) {
        console.warn("Context refresh failed:", e);
      }

      localStorage.setItem("personalProgress.selectSeasonBeforeCommunity", "1");
      setCurrentView("PersonalProgress");
    } catch (err) {
      console.error("Failed to save community changes", err);
      alert("Unable to save changes right now. Please try again.");
    }
  }

  /* ---------------- GROUP BY CATEGORY ---------------- */

  const grouped = communities.reduce((acc, c) => {
    if (!acc[c.category]) acc[c.category] = [];
    acc[c.category].push(c);
    return acc;
  }, {});

  if (loading) {
    return <div style={{ padding: "2rem" }}>Loading communities…</div>;
  }

  /* ---------------- CATEGORY ORDER ---------------- */

  const CATEGORY_ORDER = [
    "Physical Health",
    "Mental Health",
    "Spiritual Health",
    "Social Health",
  ];

  /* ---------------- UI ---------------- */

  return (
    <div>
      <div style={styles.page}>
        <img
          src={(process.env.PUBLIC_URL || "") + "/Icon-JoinCom-Blue.png"}
          alt="Logo"
          style={{
            width: "100%",
            maxWidth: 200,
            height: "auto",
            display: "block",
            margin: "20px auto 30px auto",
          }}
        />
        <h1 style={styles.centerTitle}>Communities</h1>

        <p style={styles.centerDescription}>
          Join the communities that can help you be successful with your health
          plan. These communities include discussions, encouragement, and shared
          growth experiences.
        </p>

        {CATEGORY_ORDER.filter((category) => grouped[category]).map(
          (category) => {
            const items = grouped[category];
            return (
              <section key={category} style={styles.card}>
                <h2>{category}</h2>

                <div style={styles.buttonGrid}>
                  {items.map((community) => {
                    const joined = selectedCommunityIDs.includes(community.id);
                    const revoked = revokedCommunityIDs.includes(community.id);

                    return (
                      <button
                        key={community.id}
                        onClick={() => toggleCommunity(community)}
                        disabled={revoked}
                        style={{
                          ...styles.communityButton,
                          ...(joined ? styles.communityButtonSelected : {}),
                          ...(revoked
                            ? {
                                opacity: 0.55,
                                cursor: "not-allowed",
                              }
                            : {}),
                        }}
                      >
                        <span style={styles.communityName}>
                          {community.name}
                        </span>
                        <span style={styles.subLabel}>
                          {revoked
                            ? "Access Revoked"
                            : joined
                              ? "Selected"
                              : "Join"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          },
        )}

        {/* ---------- BOTTOM BAR ---------- */}
        <div style={styles.stickyBar}>
          <label style={styles.confirmation}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />{" "}
            I understand that communities are spaces of respect, care, and
            shared responsibility.
          </label>

          <button
            disabled={!confirmed || selectedCommunityIDs.length === 0}
            onClick={saveChangesAndContinue}
            style={{
              ...styles.primaryButton,
              opacity: confirmed && selectedCommunityIDs.length > 0 ? 1 : 0.5,
            }}
          >
            Save Changes and Continue
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- STYLES ---------------- */

const styles = {
  page: {
    width: 350,
    minWidth: 300,
    backgroundColor: "white",
    padding: 24,
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    color: "#333",
  },

  centerTitle: {
    textAlign: "center",
    fontSize: "2rem",
  },
  centerDescription: {
    textAlign: "center",
    color: Theme.colors.muted,
    maxWidth: "100%",
    margin: "0 auto 2rem",
  },
  card: {
    background: Theme.colors.card,
    padding: "1.5rem",
    borderRadius: Theme.radius,
    marginBottom: "1.5rem",
  },
  buttonGrid: {
    display: "grid",
    gap: "0.75rem",
  },
  communityButton: {
    padding: "0.75rem 1rem",
    borderRadius: "999px",
    border: `1px solid ${Theme.colors.border}`,
    background: "#F9FAFB",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    textAlign: "left",
  },
  communityName: {
    flex: 1,
    textAlign: "left",
  },
  communityButtonSelected: {
    background: Theme.colors.highlight,
    fontWeight: 600,
  },
  subLabel: {
    fontSize: "0.75rem",
    color: Theme.colors.muted,
  },
  stickyBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: Theme.colors.card,
    borderTop: `1px solid ${Theme.colors.border}`,
    padding: "1rem 2rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 1000,
  },
  confirmation: {
    fontSize: "0.9rem",
    maxWidth: "65%",
  },
  primaryButton: {
    background: Theme.colors.primary,
    color: "#fff",
    border: "none",
    borderRadius: "999px",
    padding: "0.75rem 1.5rem",
    cursor: "pointer",
  },
};
